import ImageService from '../services/ImageService';
import UserService from '../services/UserService';
import type { AuthTokenResponse } from '../services/AuthService';
import { clearStorage } from '../utils';

type SessionIdentity = { id?: number | string; email?: string };

const TOKEN_STORAGE_KEYS: Array<keyof AuthTokenResponse> = [
  'accessToken',
  'accessTokenExpiredAt',
  'refreshToken',
  'refreshTokenExpiredAt',
];

const setOptionalStorageValue = (key: string, value: unknown) => {
  if (value === null || value === undefined) {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(key, String(value));
};

const getAuthorities = (accessToken: string): string[] => {
  const encodedPayload = accessToken.split('.')[1];
  if (!encodedPayload) throw new Error('Geçersiz erişim tokenı.');
  const normalizedPayload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
  const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=');
  const payload = JSON.parse(window.atob(paddedPayload));
  return Array.isArray(payload?.authorities)
    ? payload.authorities.map((authority: any) => authority?.authority).filter(Boolean)
    : [];
};

export const storeAuthTokens = (tokens: AuthTokenResponse) => {
  TOKEN_STORAGE_KEYS.forEach((key) => {
    if (!tokens?.[key]) throw new Error('Kimlik doğrulama yanıtı eksik.');
  });
  TOKEN_STORAGE_KEYS.forEach((key) => localStorage.setItem(key, String(tokens[key])));
};

const storeCurrentUser = async (response: any): Promise<SessionIdentity> => {
  const user = response?.data;
  if (!user) throw new Error('Kullanıcı bilgileri alınamadı.');

  setOptionalStorageValue('firstName', user.firstName);
  setOptionalStorageValue('lastName', user.lastName);
  setOptionalStorageValue('email', user.email);
  setOptionalStorageValue('phoneNumber', user.phoneNumber);
  setOptionalStorageValue('id', user.id);
  setOptionalStorageValue('profilePhoto', user.profilePhoto);
  setOptionalStorageValue('isPartner', user.isPartner);

  const profileFields = ['firstName', 'lastName', 'email', 'phoneNumber', 'iban', 'address', 'identityNumber'];
  const completedFieldCount = profileFields.filter((field) => Boolean(user[field])).length;
  localStorage.setItem('profileCompletion', String((completedFieldCount / profileFields.length) * 100));

  const roles = Array.isArray(user.roles) ? user.roles : [];
  const permissions = roles.flatMap((role: any) =>
    Array.isArray(role?.permissions)
      ? role.permissions.map((permission: any) => permission?.name).filter(Boolean)
      : []
  );
  localStorage.setItem('roles', JSON.stringify(roles));
  localStorage.setItem('permissions', JSON.stringify(permissions));

  localStorage.removeItem('profilePhotoBase64');
  if (user.profilePhoto) {
    try {
      const imageResponse: any = await ImageService.getImage(user.profilePhoto);
      const imageUrl = imageResponse?.data?.preSignUrl;
      if (imageUrl) localStorage.setItem('profilePhotoBase64', imageUrl);
    } catch {
      // Profil görseli oturum açılması için zorunlu değildir.
    }
  }

  return { id: user.id, email: user.email };
};

export const initializeAuthSession = async (tokens: AuthTokenResponse): Promise<SessionIdentity> => {
  try {
    storeAuthTokens(tokens);
    const authorities = getAuthorities(tokens.accessToken);
    const useUserEndpoint = authorities.includes('ROLE_ADMIN') || authorities.includes('ROLE_INSPECTOR');
    const response = useUserEndpoint ? await UserService.getMeUser() : await UserService.getMe();
    return await storeCurrentUser(response);
  } catch (error) {
    clearStorage();
    throw error;
  }
};

